import React from 'react'
import dynamic from 'next/dynamic'
import { useUpdateEffect } from 'ahooks'
import { Skeleton } from 'antd'

import type { $Enums } from '@prisma/client'

import {
  EPricingStep,
  type TUsePayStep,
} from '@/contexts/PlanContext/hooks/payStep'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { getProductItems } from '@/contexts/PlanContext/helpers/getProductItems'
import { PaymentPlatformPlatformName } from '@/contexts/SiteSpecificContext/types'
import { usePaidSuccessfullyUrl } from '@/contexts/PlanContext/hooks/paidSuccess/paidSuccessfullyUrl'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'


import { useMemo, useState, useEffect } from 'react';
import { tw, cls } from 'tagged-classnames-free';
export interface IPaymentContentProps {
  className?: string
  payStepInfo: TUsePayStep
  isActive?: boolean
}

const PaypalPayment = dynamic(() => import('./PaypalPayment'), {
  ssr: false,
})

const FastSpringPayment = dynamic(() => import('./FastSpringPayment'), {
  ssr: false,
})

const StripePayment = dynamic(() => import('./StripePayment'), {
  ssr: false,
})

const PayinsiderPayment = dynamic(() => import('./PayinsiderPayment'), {
  ssr: false,
})

const PaymentContent: React.FC<IPaymentContentProps> = (props) => {
  const { className, isActive, payStepInfo } = props

  const {
    curClientSecret,
    curPaidProduct,
    getPaymentLinkLoading,
    onChangeBuyStep,
    isModal,
    step,
    appName,
    paymentPlatformInfo,
  } = payStepInfo
  // console.log('🚀 ~ curClientSecret:', curClientSecret)

  const {
    paymentPlatformList,
    firstPaymentPlatform,
    firstPaymentPlatformIsStripe,
    firstPaymentPlatformIsFastspring,
    fastspringPlatForm,
    paypalPlatForm,
    stripePlatForm,
    firstPaymentPlatformIsPayinsider,
  } = paymentPlatformInfo

  const { upgradePlan } = useAppUIModal()
  const { getPaidSuccessfullyUrl } = usePaidSuccessfullyUrl()
  const { isSignIn } = useSessionInfo()

  const modalOpen = useMemo(() => {
    return upgradePlan.open
  }, [upgradePlan.open])

  const [shouldUpdate, setShouldUpdate] = useState(false)

  /* 弹窗关闭，重置一下付费组件 */
  useUpdateEffect(() => {
    if (isModal && !upgradePlan.open) {
      setShouldUpdate(true)
    }
  }, [upgradePlan.open])

  useEffect(() => {
    if (shouldUpdate) {
      setTimeout(() => {
        setShouldUpdate(false)
      }, 10)
    }
  }, [shouldUpdate])

  const shouldHidden = useMemo(() => {
    return (!isModal && modalOpen) || shouldUpdate
  }, [isModal, modalOpen, shouldUpdate])

  useEffect(() => {
    if (shouldHidden && step === EPricingStep.PAY) {
      onChangeBuyStep()
    }
  }, [shouldHidden, step])

  useEffect(() => {
    if (firstPaymentPlatformIsPayinsider && isSignIn && !curClientSecret) {
      onChangeBuyStep()
    }
  }, [curClientSecret, firstPaymentPlatformIsPayinsider, isSignIn])

  const productStr = useMemo(() => {
    const items = getProductItems({
      productPriceList: curPaidProduct?.productPriceList || [],
      appName,
    })
    const itemsStr = JSON.stringify(items)
    return itemsStr
  }, [curPaidProduct, appName])

  const paypalSuccessUrl = useMemo(() => {
    return getPaidSuccessfullyUrl({
      items: productStr,
      payment_platform: PaymentPlatformPlatformName.Paypal,
    })
  }, [productStr])

  const { pricesId, pricesIdStr } = useMemo(() => {
    const pricesId = curPaidProduct?.productPriceList.map(
      (item) => item.priceId,
    )
    const pricesIdStr = JSON.stringify(pricesId)
    return { pricesId, pricesIdStr }
  }, [curPaidProduct?.productPriceList])

  const paidSuccessUrl = useMemo(() => {
    return getPaidSuccessfullyUrl({
      // items: productStr,
      pricesId: pricesIdStr,
      payment_platform: firstPaymentPlatform.platformName,
      action: 'create',
    })
  }, [firstPaymentPlatform.platformName, pricesIdStr])

  const onPaymentSuccess = ({
    platformName,
    orderId,
  }: {
    platformName: $Enums.PaymentPlatformPlatformName
    orderId: string
  }) => {
    const paidSuccessfullyUrl = getPaidSuccessfullyUrl({
      items: productStr,
      payment_platform: platformName,
      order_Id: orderId,
    })
    window.location.href = paidSuccessfullyUrl
  }

  if (step === EPricingStep.BUY) {
    return null
  }

  if (shouldHidden) {
    return null
  }

  if (!isActive) {
    return null
  }

  return (
    <div
      className={tw`
        ${className}
        min-h-[647px]
      `}
    >
      {/* <div className='flex items-center gap-x-3'>
        {paypalPlatForm?.platformName ? (
          <PaypalPayment
            className='hover:text-primary cursor-pointer rounded-t-md px-8 py-2 text-sm font-semibold'
            isCustom
            pricesId={curPaidProduct?.pricesId}
            successUrl={paypalSuccessUrl}
          />
        ) : null}
      </div> */}
      <div
        className={cls`
          rounded-md
          py-6
        `}
      >
        <Skeleton
          paragraph={{ rows: 8 }}
          title={false}
          active
          loading={getPaymentLinkLoading}
        >
          {firstPaymentPlatformIsStripe && curClientSecret ? (
            <StripePayment
              key={`${curClientSecret}`}
              publishableKey={firstPaymentPlatform.publishableKey}
              loading={getPaymentLinkLoading || shouldUpdate}
              clientSecret={curClientSecret}
            />
          ) : null}
          {firstPaymentPlatformIsFastspring && curClientSecret ? (
            <FastSpringPayment
              publishableKey={firstPaymentPlatform.platformName}
              sessionId={curClientSecret || ''}
              onPaymentSuccess={({ orderId }) => {
                onPaymentSuccess({
                  orderId,
                  platformName: PaymentPlatformPlatformName.Fastspring,
                })
              }}
            />
          ) : null}
          {firstPaymentPlatformIsPayinsider && curClientSecret ? (
            <PayinsiderPayment
              payStepInfo={payStepInfo}
              key={`${curClientSecret}`}
              paidSuccessUrl={paidSuccessUrl}
              accessToken={curClientSecret}
            />
          ) : null}
        </Skeleton>
      </div>
    </div>
  )
}

export default PaymentContent
